import type { ReactNode } from 'react';
import type { AuditEvent, AuditFieldType } from '@/types/audit';

/**
 * Translation function shape — matches `useTranslation().t`.
 * Defined locally so the registry stays free of React-runtime imports.
 */
export type TFn = (key: string, fallback?: string) => string;

/**
 * Default title humanizer.
 *   EMPLOYMENT_TYPE_UPDATED → "Employment type updated"
 *   PAYROLL_REJECTED        → "Payroll rejected"
 *   FOO                     → "Foo"
 */
export function defaultTitle(event: AuditEvent): string {
  const lower = event.eventName.toLowerCase().replaceAll('_', ' ').trim();
  if (!lower) return event.eventName;
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * Format a single field-change value for display in the row body.
 * `REFERENCE` falls through; row component owns catalog lookup.
 */
export function formatFieldValue(
  value: unknown,
  fieldType: AuditFieldType,
  t: TFn,
): string {
  if (value === null || value === undefined || value === '') {
    return t('K_AUDIT_VALUE_EMPTY', '—');
  }
  switch (fieldType) {
    case 'BOOLEAN':
      // Reuses the global K_YES / K_NO keys rather than declaring
      // audit-specific duplicates.
      return value ? t('K_YES', 'Yes') : t('K_NO', 'No');
    case 'NUMBER':
      if (typeof value === 'number') return value.toLocaleString();
      return String(value);
    case 'DATE':
    case 'DATETIME':
    case 'TIME':
      return formatDateLike(value);
    case 'JSON':
      return formatJsonValue(value, t);
    case 'FORM':
      return t('K_AUDIT_VALUE_FORM_SCHEMA', '(form schema)');
    case 'TEXT':
    case 'UUID':
    case 'REFERENCE':
    default:
      return String(value);
  }
}

function formatDateLike(value: unknown): string {
  if (typeof value !== 'string' && typeof value !== 'number') return String(value);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

/**
 * Default JSON-field renderer.
 *
 * For arrays we try to extract a human-readable label from each item:
 *   - string / number / boolean → its string form
 *   - object → first of name | code | label | title that is a string
 * Recognised items are joined with ", ". If we can't extract anything
 * (e.g. the array contains opaque blobs), fall back to the "(updated)"
 * placeholder rather than dumping raw JSON.
 *
 * For non-array JSON (objects, scalars), we currently always show
 * "(updated)". Page-specific fields that need richer rendering should
 * register an entry in `eventOverrides` keyed on the event_name and
 * override `renderBody` there — see the example block above.
 */
function formatJsonValue(value: unknown, t: TFn): string {
  if (Array.isArray(value)) {
    const labels: string[] = [];
    for (const item of value) {
      const label = extractItemLabel(item);
      if (label === null) return t('K_AUDIT_VALUE_JSON_UPDATED', '(updated)');
      labels.push(label);
    }
    if (labels.length === 0) return t('K_AUDIT_VALUE_EMPTY', '—');
    return labels.join(', ');
  }
  return t('K_AUDIT_VALUE_JSON_UPDATED', '(updated)');
}

function extractItemLabel(item: unknown): string | null {
  if (item === null || item === undefined) return null;
  if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
    return String(item);
  }
  if (typeof item === 'object') {
    const obj = item as Record<string, unknown>;
    if (typeof obj.name === 'string' && obj.name) return obj.name;
    if (typeof obj.code === 'string' && obj.code) return obj.code;
    if (typeof obj.label === 'string' && obj.label) return obj.label;
    if (typeof obj.title === 'string' && obj.title) return obj.title;
  }
  return null;
}

/**
 * Per-event override entry. Empty registry on day 1 — add keyed by eventName
 * when a backend event needs custom copy or non-diff body rendering.
 *
 *   Example:
 *   PAYROLL_REJECTED: {
 *     renderBody: (e, t) => e.filterMap?.reason ?? null,
 *   },
 */
export interface EventOverride {
  renderTitle?: (event: AuditEvent, t: TFn) => string;
  renderBody?: (event: AuditEvent, t: TFn) => ReactNode;
}

export const eventOverrides: Record<string, EventOverride> = {};
